import { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import styled from "styled-components";
import { Button, Dialog, Input } from "antd-mobile";
import useNavigation from "../../hooks/useNavigation";
import PageWrap from "../components/PageWrap";
import PageHeader, { headerHeight } from "../components/PageHeader";
import { useStores } from "../../models";
import { ToastError, ToastSuccess } from "../../components/Toast";
import useCheckBinding from "../../hooks/useCheckBinding";

const ScrollArea = styled.div`
  height: calc(100% - ${headerHeight});
  overflow: auto;
  background: #fff;
  padding: 12px;
`;

const Field = styled.div`
  margin-bottom: 12px;
`;

const Label = styled.div`
  font-size: 13px;
  color: #666;
  margin-bottom: 6px;
`;

const SettingPayNumber = () => {
  const { goBack, navigate } = useNavigation();
  const { apiStore, authStore, walletStore } = useStores();
  const { handleCheckBindingPhone } = useCheckBinding();
  const isUpdate = !!walletStore.pay_pwd;
  const [payPwd, setPayPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [secretSign, setSecretSign] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [btnText, setBtnText] = useState("获取验证码");
  const [counting, setCounting] = useState(false);
  const timerRef = useRef<any>(null);
  const phone = walletStore.phone;
  const onlyDigits = (v: string, max?: number) => {
    const digits = v.replace(/\D/g, "");
    return typeof max === "number" ? digits.slice(0, max) : digits;
  };

  useEffect(() => {
    // 未绑定手机时直接去绑定页，避免在本页弹窗后再跳转导致弹窗残留
    if (!walletStore.is_phone_verify && !walletStore.phone) {
      navigate("PhoneBinding", undefined, { replace: true });
      return () => clearInterval(timerRef.current);
    }
    handleCheckBindingPhone().catch(() => undefined);
    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startCount = () => {
    let n = 60;
    setCounting(true);
    setBtnText(`${n}s后重新获取`);
    timerRef.current = setInterval(() => {
      n -= 1;
      if (n > 0) setBtnText(`${n}s后重新获取`);
      else {
        clearInterval(timerRef.current);
        setCounting(false);
        setBtnText("获取验证码");
      }
    }, 1000);
  };

  const sendSms = async () => {
    if (counting) return;
    if (!phone) {
      Dialog.clear();
      navigate("PhoneBinding");
      return;
    }
    try {
      const api = apiStore.getPhoneSmsv1 || apiStore.getPhoneSms;
      const res = await api({
        token: authStore.getToken,
        type: 3,
        phone,
        country_code: "86",
      });
      if (res?.code === 200 || res?.code === 0) {
        ToastSuccess("发送成功");
        startCount();
      } else ToastError((res as any)?.message || "发送失败");
    } catch (e: any) {
      ToastError(e.message);
    }
  };

  const submit = async () => {
    if (!/^\d{6}$/.test(payPwd)) return ToastError("请输入6位数字支付密码");
    if (payPwd !== confirm) return ToastError("两次密码不一致");
    if (!secretSign) return ToastError(isUpdate ? "请输入新支付密码" : "请输入安全口令");
    if (!code) return ToastError("请输入验证码");
    try {
      setLoading(true);
      const res = isUpdate
        ? await apiStore.updatePaypws({
            token: authStore.getToken,
            old_pay_pwd: payPwd,
            new_pay_pwd: secretSign,
            confirm,
            phone,
            code,
            country_code: "86",
          })
        : await apiStore.paypws({
            token: authStore.getToken,
            pay_pwd: payPwd,
            confirm,
            pay_pwd_secret_sign: secretSign,
            phone,
            code,
            country_code: "86",
          });
      if (res?.code === 200 || res?.code === 0) {
        walletStore.setPay_pwd?.();
        ToastSuccess(isUpdate ? "修改成功" : "设定成功");
        goBack(-1);
      } else ToastError((res as any)?.message || "失败");
    } catch (e: any) {
      ToastError(e.message || "操作失败");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageWrap>
      <PageHeader title={isUpdate ? "修改支付密码" : "设置支付密码"} />
      <ScrollArea>
        <Field>
          <Label>{isUpdate ? "原支付密码" : "支付密码（6位数字）"}</Label>
          <Input
            type="password"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            value={payPwd}
            onChange={(v) => setPayPwd(onlyDigits(v, 6))}
            placeholder={isUpdate ? "请输入原支付密码" : "请输入支付密码"}
            style={{ background: "#f7f8ff", padding: 8 }}
          />
        </Field>
        <Field>
          <Label>{isUpdate ? "新支付密码" : "确认支付密码"}</Label>
          <Input
            type="password"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            value={isUpdate ? secretSign : confirm}
            onChange={(v) =>
              isUpdate ? setSecretSign(onlyDigits(v, 6)) : setConfirm(onlyDigits(v, 6))
            }
            placeholder={isUpdate ? "请输入新支付密码" : "请再次输入支付密码"}
            style={{ background: "#f7f8ff", padding: 8 }}
          />
        </Field>
        {isUpdate ? (
          <Field>
            <Label>确认新支付密码</Label>
            <Input
              type="password"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              value={confirm}
              onChange={(v) => setConfirm(onlyDigits(v, 6))}
              placeholder="请再次输入新支付密码"
              style={{ background: "#f7f8ff", padding: 8 }}
            />
          </Field>
        ) : (
          <Field>
            <Label>安全口令</Label>
            <Input
              value={secretSign}
              onChange={setSecretSign}
              placeholder="请输入安全口令"
              style={{ background: "#f7f8ff", padding: 8 }}
            />
          </Field>
        )}
        <Field>
          <Label>手机验证码（{phone ? phone.replace(/(\d{3})\d{4}(\d{4})/, "$1****$2") : "未绑定"}）</Label>
          <div style={{ display: "flex", gap: 8 }}>
            <Input
              value={code}
              onChange={setCode}
              placeholder="请输入验证码"
              style={{ background: "#f7f8ff", padding: 8, flex: 1 }}
            />
            <Button size="small" color="primary" fill="outline" disabled={counting} onClick={sendSms}>
              {btnText}
            </Button>
          </div>
        </Field>
        <Button color="primary" block loading={loading} onClick={submit}>
          提交
        </Button>
      </ScrollArea>
    </PageWrap>
  );
};

export default observer(SettingPayNumber);
